/*
 * Copyright (c) 2021 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import {
    EntirePeriodTurnover,
    TurnoversByPeriodWithParams,
    TurnoversByPeriodGroupType,
    TurnoversByPeriodQueryParams,
} from '../../../data/api/apiCommon.typings';
import { splitDisplayedPeriod } from 'components/RegistryWidgets/Common/TurnoverConfig';

export const parceTurnoverDate = (date: string, valueType: TurnoversByPeriodGroupType): string => {
    const [day, month, year] = date.split('-');
    switch (valueType) {
        case TurnoversByPeriodGroupType.YEAR:
            return year;
        case TurnoversByPeriodGroupType.MONTH:
            return month;
        case TurnoversByPeriodGroupType.DAY:
            return day;
    }
};

export const updateEntirePeriodTurnoverWithNewData = (
    entireTurnovers: EntirePeriodTurnover,
    data: TurnoversByPeriodWithParams,
): EntirePeriodTurnover => {
    let newEntireTurnovers: EntirePeriodTurnover;

    if (data.params.group === TurnoversByPeriodGroupType.YEAR) {
        // turnovers for the entire period
        newEntireTurnovers = { yearsTurnovers: [] };

        if (data.turnovers) {
            newEntireTurnovers.yearsTurnovers = data.turnovers.map((item) => {
                return {
                    yearSum: item,
                    year: parceTurnoverDate(item.date, TurnoversByPeriodGroupType.YEAR),
                    isHiddenMonthsList: true,
                    monthsTurnovers: [] as any,
                };
            });
        }
        return newEntireTurnovers;
    }

    newEntireTurnovers = { ...entireTurnovers };

    if (data.params.group === TurnoversByPeriodGroupType.MONTH) {
        //turnovers for some year
        const yearIndex = newEntireTurnovers.yearsTurnovers.findIndex((element) => element.year === data.params.year);
        if (yearIndex !== -1) {
            newEntireTurnovers.yearsTurnovers[yearIndex].monthsTurnovers = [];
            if (data.turnovers) {
                newEntireTurnovers.yearsTurnovers[yearIndex].monthsTurnovers = data.turnovers.map((item) => {
                    return {
                        monthSum: item,
                        month: parceTurnoverDate(item.date, TurnoversByPeriodGroupType.MONTH),
                        isHiddenDaysList: true,
                        daysTurnovers: [] as any[],
                    };
                });
            }
            newEntireTurnovers.yearsTurnovers[yearIndex].isHiddenMonthsList = false;
            return newEntireTurnovers;
        }
    }

    if (data.params.group === TurnoversByPeriodGroupType.DAY) {
        //turnovers for some month
        const yearIndex = newEntireTurnovers.yearsTurnovers.findIndex((year) => year.year === data.params.year);
        if (yearIndex !== -1) {
            const monthIndex = newEntireTurnovers.yearsTurnovers[yearIndex].monthsTurnovers.findIndex(
                (month) => month.month === data.params.month,
            );
            if (monthIndex !== -1) {
                newEntireTurnovers.yearsTurnovers[yearIndex].monthsTurnovers[monthIndex].daysTurnovers = [];
                if (data.turnovers) {
                    newEntireTurnovers.yearsTurnovers[yearIndex].monthsTurnovers[monthIndex].daysTurnovers =
                        data.turnovers.map((item) => {
                            return {
                                daySum: item,
                                day: parceTurnoverDate(item.date, TurnoversByPeriodGroupType.DAY),
                            };
                        });
                }
                newEntireTurnovers.yearsTurnovers[yearIndex].monthsTurnovers[monthIndex].isHiddenDaysList = false;
                return newEntireTurnovers;
            }
        }
    }
    return newEntireTurnovers;
};

export const collapseEntirePeriodTurnovers = (entireTurnovers: EntirePeriodTurnover): EntirePeriodTurnover => {
    const newEntireTurnovers = { ...entireTurnovers };
    newEntireTurnovers.yearsTurnovers.forEach((year) => {
        year.isHiddenMonthsList = true;
        year.monthsTurnovers.forEach((month) => {
            month.isHiddenDaysList = true;
        });
    });
    return newEntireTurnovers;
};

export const expandTurnoversPeriod = (
    entireTurnovers: EntirePeriodTurnover,
    period: string,
    isToHide: boolean,
): EntirePeriodTurnover => {
    const [periodYear, periodMonth] = splitDisplayedPeriod(period);
    const isYear = periodMonth === undefined;

    const newEntireTurnovers = { ...entireTurnovers };

    for (let i = 0; i < newEntireTurnovers.yearsTurnovers.length; i++) {
        if (newEntireTurnovers.yearsTurnovers[i].year === periodYear) {
            if (isYear) {
                newEntireTurnovers.yearsTurnovers[i].isHiddenMonthsList = isToHide;
                break;
            } else {
                for (let j = 0; j < newEntireTurnovers.yearsTurnovers[i].monthsTurnovers.length; j++) {
                    if (newEntireTurnovers.yearsTurnovers[i].monthsTurnovers[j].month === periodMonth) {
                        newEntireTurnovers.yearsTurnovers[i].monthsTurnovers[j].isHiddenDaysList = isToHide;
                        break;
                    }
                }
            }
        }
    }
    return newEntireTurnovers;
};

export const getParamsToFetchTurnoversByPeriod = (
    entireTurnovers: EntirePeriodTurnover,
    period: string,
): TurnoversByPeriodQueryParams => {
    let params: TurnoversByPeriodQueryParams = null;
    const [periodYear, periodMonth] = splitDisplayedPeriod(period);
    const isYear = periodMonth === undefined;

    for (let i = 0; i < entireTurnovers.yearsTurnovers.length; i++) {
        if (entireTurnovers.yearsTurnovers[i].year === periodYear) {
            if (isYear) {
                if (entireTurnovers.yearsTurnovers[i].monthsTurnovers.length === 0) {
                    params = { group: TurnoversByPeriodGroupType.MONTH, year: periodYear };
                }
                break;
            } else {
                for (let j = 0; j < entireTurnovers.yearsTurnovers[i].monthsTurnovers.length; j++) {
                    if (entireTurnovers.yearsTurnovers[i].monthsTurnovers[j].month === periodMonth) {
                        if (entireTurnovers.yearsTurnovers[i].monthsTurnovers[j].daysTurnovers.length === 0) {
                            params = { group: TurnoversByPeriodGroupType.DAY, year: periodYear, month: periodMonth };
                        }
                        break;
                    }
                }
            }
        }
    }
    return params;
};

export const isTurnoverByPeriodCollapsed = (entireTurnovers: EntirePeriodTurnover): boolean => {
    let isCollapsed = true;
    if (entireTurnovers && entireTurnovers.yearsTurnovers && entireTurnovers.yearsTurnovers.length > 0) {
        for (let i = 0; i < entireTurnovers.yearsTurnovers.length; i++) {
            if (!entireTurnovers.yearsTurnovers[i].isHiddenMonthsList) {
                isCollapsed = false;
                break;
            }
        }
    }
    return isCollapsed;
};
